import {isEmpty} from 'lodash';
import {EnumValues} from 'enum-values';
import {Logger} from '../utils/Logger';
import {Component} from '@angular/core';
import {Validator} from '../utils/Validator';
import {LoanReason} from '../model/loan-reason';
import {Step} from '../application-flow/steps/step';
import {ActivatedRoute, Router} from '@angular/router';
import {AnalyticsTracker} from '../utils/AnalyticsTracker';
import {UserInteractor} from '../interactors/user.interactor';
import {LoanApplication} from '../model/loan-application.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BasicLoanInfoStep} from '../application-flow/steps/basic-loan-info.step';
import {LoanApplicationInteractor} from '../interactors/loan-application.interactor';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {Keys, StorageService} from '../service/storage.service';
import {SelectedOption} from '../borrow/eligibility-check/options/option-row/selected-option';
import {CreditAssessmentService} from '../service/credit-assessment.service';
import {UserUtil} from '../utils/UserUtil';
import {LoanTenure} from '../model/loan-tenure';
import {ConfigService} from '../service/config.service';
import { IndicativeLoanOffer } from '../model/cibil/indicative-loan.model';
import { CreditBureauName } from '../model/cibil/credit-bureau.model';

@Component({
    selector: 'app-loan-info',
    templateUrl: './loan-info.component.html',
    styleUrls: ['../loan-application-flow-component/application-flow.css', './loan-info.component.css']
})
export class LoanInfoComponent extends AbstractApplicationFlowComponent {
    private static readonly DenominationForLoanApplication = 1000;
    private static readonly MaximumLoanApplicationAmount = 5 * 100 * 1000;
    private minimumLoanApplicationAmount;

    public readonly Others = LoanReason.Others;
    public loanInfoFormGroup: FormGroup;
    public readonly analyticsTracker: AnalyticsTracker;
    hasIndicativeEligibilityCheck: boolean;
    public selected: string;
    public shouldShowLoader: boolean;
    private readonly router: Router;
    private loanApplication: LoanApplication;
    private readonly userInteractor: UserInteractor;
    private readonly applicationInteractor: LoanApplicationInteractor;
    private storageService: StorageService;
    private creditAssessmentService: CreditAssessmentService;
    private readonly userUtil: UserUtil;
    private reasonDescriptionValidators = [Validators.minLength(5), Validators.maxLength(20), Validators.pattern('^[A-Za-z\']+(\\s?[A-Za-z\']+\\s*)*$')];
    public indicativeLoan: IndicativeLoanOffer;
    public amountPlaceholder: string = `Upto ₹ 5,00,000`;

    constructor(formBuilder: FormBuilder, interactor: LoanApplicationInteractor, userInteractor: UserInteractor,
                analyticsTracker: AnalyticsTracker, activatedRoute: ActivatedRoute, router: Router, navigator: LoanApplicationFlowNavigator, storageService: StorageService, creditAssessmentService: CreditAssessmentService,
                userUtil: UserUtil, private configService: ConfigService) {
        super(navigator);
        this.router = router;
        this.userInteractor = userInteractor;
        this.applicationInteractor = interactor;
        this.analyticsTracker = analyticsTracker;
        this.storageService = storageService;
        this.creditAssessmentService = creditAssessmentService;
        this.userUtil = userUtil;
        this.hasIndicativeEligibilityCheck = false;
        this.loanInfoFormGroup = formBuilder.group({
            amount: formBuilder.control(null),
            reason: formBuilder.control(null, [Validators.required]),
            reasonDescription: formBuilder.control(null, this.reasonDescriptionValidators),
            tenure: formBuilder.control(null, [Validators.required])
        });
        this.getIndicativeLoanParameter();
        activatedRoute.data.subscribe(
            async ({
                       loanApplication,
                       selectedOption
                   }: { loanApplication: LoanApplication, selectedOption: SelectedOption }) => {
                await this.setLoanAmountValidation();
                await this.onSelectedOption(selectedOption);
                this.onApplicationReceived(loanApplication);
                this.onLoanReasonChange();
            });
    }


    public onLoanReasonChange(): void {
        if (this.loanInfoFormGroup.controls.reason.value === this.Others) {
            this.loanInfoFormGroup.controls.reasonDescription.setValidators(this.reasonDescriptionValidators.concat(Validators.required));
        } else {
            this.loanInfoFormGroup.controls.reasonDescription.clearValidators();
            this.loanInfoFormGroup.controls.reasonDescription.reset();
        }
    }

    public async onSubmit(formGroup: FormGroup): Promise<void> {
        if (!this.isValid(formGroup)) {
            Logger.log('Form is invalid. Not proceeding with submission.');
            return;
        }
        this.showLoader();

        try {
            const enteredAmount = formGroup.controls.amount.value;
            const tenure = formGroup.controls.tenure.value;
            let selectedReason = formGroup.controls.reason.value;
            let application = this.loanApplication;

            if (selectedReason === LoanReason.Others) {
                selectedReason = formGroup.controls.reasonDescription.value;
            }

            if (application) {
                application.reason = selectedReason;
                application.amount = enteredAmount;
                application.tenure = tenure;
            } else {
                const userId = await this.userInteractor.getUserId();
                const channelPartnerId = await this.storageService.getObjectFromSession<string>(Keys.PARTNER_ID);
                application = new LoanApplication(enteredAmount, selectedReason, userId, tenure, channelPartnerId);
            }
            if (this.userUtil.isUserLoggedIn() && application.id) {
                await this.applicationInteractor.update(application);
            } else {
                await this.applicationInteractor.saveApplicationToStorage(application);
            }
            await this.nextPage();
        } finally {
            await this.hideLoader();
        }
    }

    public reasons(): string[] {
        return EnumValues.getValues(LoanReason);
    }

    public tenures(): number[] {
        return EnumValues.getValues(LoanTenure);
    }

    public showLoader() {
        this.shouldShowLoader = true;
    }

    public hideLoader() {
        this.shouldShowLoader = false;
    }

    step(): new(...args: any[]) => Step {
        return BasicLoanInfoStep;
    }

    public onApplicationReceived(loanApplication: LoanApplication) {
        this.loanApplication = loanApplication;
        if (!isEmpty(loanApplication)) {
            let reasonDescription = null;
            if (!this.isLoanReasonAvailable(loanApplication.reason)) {
                reasonDescription = loanApplication.reason;
                loanApplication.reason = this.Others;
            }
            this.loanInfoFormGroup.patchValue({
                amount: loanApplication.amount,
                reason: loanApplication.reason,
                reasonDescription: reasonDescription,
                tenure: loanApplication.tenure.toString()
            });
        }
    }

    isLoanReasonAvailable(reason: string): boolean {
        return Object.values(LoanReason).includes(reason as LoanReason);
    }

    protected async setLoanAmountValidation(): Promise<void> {
        const channelPartnerId = await this.storageService.getObjectFromSession<string>(Keys.PARTNER_ID);
        this.minimumLoanApplicationAmount = isEmpty(channelPartnerId) ? this.configService.getMinimumLoanAmount() :
            this.configService.getMinimumLoanAmountForPartner();
        this.loanInfoFormGroup.controls.amount.setValidators([
            Validators.min(this.minimumLoanApplicationAmount),
            Validators.required,
            Validators.max(this.indicativeLoan?.amount || LoanInfoComponent.MaximumLoanApplicationAmount),
            Validator.denominationOf(LoanInfoComponent.DenominationForLoanApplication)]);
    }

    protected async onSelectedOption(selectedOption: SelectedOption): Promise<void> {
        if (!isEmpty(selectedOption)) {
            const selectedCreditOption = await this.creditAssessmentService.getCreditOptionById(selectedOption.getCreditOptionId());
            this.loanInfoFormGroup.patchValue({
                amount: selectedCreditOption.amount,
                tenure: selectedCreditOption.tenure.toString()
            });
            this.hasIndicativeEligibilityCheck = true;
            this.loanInfoFormGroup.controls.tenure.disable();
        }
    }

    protected errorMessages() {
        return {
            amount: [{
                type: 'required',
                message: 'Please enter the loan amount'
            }, {
                type: 'min',
                message: 'The minimum loan amount is ₹' + this.minimumLoanApplicationAmount
            }, {
                type: 'max',
                message: 'The maximum loan amount is ₹' + (this.indicativeLoan?.amount || LoanInfoComponent.MaximumLoanApplicationAmount)
            }, {
                type: 'denomination',
                message: 'The loan amount can only be in the multiples of ₹' + LoanInfoComponent.DenominationForLoanApplication
            }],
            reason: [{
                type: 'required',
                message: 'Please select a purpose of the loan'
            }],
            reasonDescription: [{
                type: 'required',
                message: 'Please elaborate the purpose of the loan'
            }, {
                type: 'minlength',
                message: 'Too short of a description'
            }, {
                type: 'maxlength',
                message: 'Max characters allowed is 20'
            }, {
                type: 'pattern',
                message: 'Only A to Z and \' characters allowed'
            }],
            tenure: [{
                type: 'required',
                message: 'Please select the tenure of the loan'

            }]
        };
    }

    private getIndicativeLoanParameter(): void {
        if (this.router.getCurrentNavigation() && this.router.getCurrentNavigation().extras.state) {
            const { amount, tenure, createLoanApplicationType, creditReportId, creditOptionId } = this.router.getCurrentNavigation().extras.state;
            this.indicativeLoan = {amount, tenure, rateOfInterest: null, emi: null, creditOptionId: null};
            this.loanInfoFormGroup.patchValue({
                amount: amount,
                tenure: tenure.toString()
            });
            this.amountPlaceholder = `Upto ₹ ${amount}`;
            if((createLoanApplicationType === CreditBureauName.CIBIL) && creditReportId && creditOptionId) {
                this.storageService.saveItem(Keys.IS_LOAN_APPLICATION_COMING_FROM_INDICATIVE_LOAN, "true");
                this.storageService.saveItem(Keys.CREDIT_REPORT_ID, creditReportId);
                this.storageService.saveItem(Keys.CREDIT_OPTION_ID, creditOptionId);
            } else {
                this.storageService.removeItem(Keys.IS_LOAN_APPLICATION_COMING_FROM_INDICATIVE_LOAN);
                this.storageService.removeItem(Keys.CREDIT_REPORT_ID);
                this.storageService.removeItem(Keys.CREDIT_OPTION_ID);
            }
        }
    }
}
